import { CartLinesUpdateEvent } from '@shopify/events';

const cache = new Map();
const SIZE_RE = /talla|size|tamaño/i;

function getProduct(handle) {
  if (!cache.has(handle)) {
    cache.set(
      handle,
      fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error('product fetch failed');
        return r.json();
      })
    );
  }
  return cache.get(handle);
}

function handleFromCard(card) {
  const a = card.querySelector('a[href*="/products/"]');
  if (!a) return null;
  const path = new URL(a.getAttribute('href'), location.origin).pathname;
  const part = path.split('/products/')[1];
  return part ? part.split(/[/?#]/)[0] : null;
}

function optionName(o) {
  return typeof o === 'string' ? o : o?.name || '';
}

async function enhance(card) {
  if (card.dataset.vsaQs) return;
  card.dataset.vsaQs = 'pending';
  const handle = handleFromCard(card);
  if (!handle) return;
  let product;
  try {
    product = await getProduct(handle);
  } catch {
    return;
  }
  if (!product?.variants?.length) return;
  const single = product.variants.length === 1;
  if (!single && (product.options.length !== 1 || !SIZE_RE.test(optionName(product.options[0])))) return;
  const soldOut = !product.available;

  const row = document.createElement('div');
  row.className = 'vsa-qs';
  const label = document.createElement('span');
  label.className = 'vsa-qs__label';
  label.textContent = soldOut ? 'Agotado' : single ? 'Talla única' : 'Agregar talla';
  if (soldOut) row.classList.add('is-soldout');
  const sizes = document.createElement('div');
  sizes.className = 'vsa-qs__sizes';

  for (const v of product.variants) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'vsa-qs__btn';
    b.textContent = single ? 'Agregar' : v.option1;
    b.disabled = !v.available;
    b.setAttribute('aria-label', single ? `Agregar ${product.title} al carrito` : `Agregar ${product.title} talla ${v.option1} al carrito`);
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      addToCart(card, b, v.id, row);
    });
    sizes.append(b);
  }
  const done = document.createElement('span');
  done.className = 'vsa-qs__done';
  done.textContent = 'Agregado al carrito';
  row.append(label, sizes, done);
  ['click', 'pointerdown', 'touchstart'].forEach((t) => row.addEventListener(t, (e) => e.stopPropagation(), { passive: t === 'touchstart' }));

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'vsa-qs__toggle';
  toggle.setAttribute('aria-label', soldOut ? `${product.title} agotado` : `Elegir talla de ${product.title}`);
  toggle.innerHTML = '<span></span><span></span>';
  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.querySelectorAll('.vsa-qs-open').forEach((c) => c !== card && c.classList.remove('vsa-qs-open'));
    card.classList.toggle('vsa-qs-open');
  });
  ['pointerdown', 'touchstart'].forEach((t) => toggle.addEventListener(t, (e) => e.stopPropagation(), { passive: t === 'touchstart' }));

  const gallery = card.querySelector('.card-gallery') || card.querySelector('.product-card__content') || card;
  gallery.classList.add('vsa-qs-host');
  gallery.append(row, toggle);
  card.classList.add('vsa-qs-ready');
  card.dataset.vsaQs = 'done';
}

async function addToCart(card, btn, variantId, row) {
  if (btn.dataset.busy) return;
  btn.dataset.busy = '1';
  btn.classList.add('is-loading');
  const routes = window.Theme?.routes || { cart_add_url: '/cart/add.js', cart_url: '/cart' };
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((c) => c.dataset.sectionId)
    .filter(Boolean)
    .join(',');
  const deferred = CartLinesUpdateEvent.createPromise();
  card.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections }),
    }).then((r) => r.json());
    const cart = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' }).then((r) => r.json());
    const didError = Boolean(res.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'vsa-quick-size', itemCount: 1, sections: res.sections, didError },
    });
    if (didError) {
      flash(btn, '—', 'is-error');
    } else {
      row?.classList.add('is-done');
      setTimeout(() => {
        row?.classList.remove('is-done');
        card.classList.remove('vsa-qs-open');
      }, 1600);
    }
  } catch (err) {
    deferred.reject(err);
    flash(btn, '!', 'is-error');
  } finally {
    delete btn.dataset.busy;
    btn.classList.remove('is-loading');
  }
}

function flash(btn, text, cls) {
  const original = btn.textContent;
  btn.textContent = text;
  btn.classList.add(cls);
  setTimeout(() => {
    btn.textContent = original;
    btn.classList.remove(cls);
  }, 1400);
}

function scan(root = document) {
  root.querySelectorAll('product-card:not([data-vsa-qs])').forEach((card) => {
    if (card.closest('.quick-add-modal, dialog, .predictive-search, header')) return;
    enhance(card);
  });
}

scan();
document.addEventListener('click', (e) => {
  if (!e.target.closest?.('.vsa-qs, .vsa-qs__toggle')) document.querySelectorAll('.vsa-qs-open').forEach((c) => c.classList.remove('vsa-qs-open'));
});
new MutationObserver((muts) => {
  for (const m of muts) {
    for (const n of m.addedNodes) {
      if (n.nodeType !== 1) continue;
      if (n.matches?.('product-card') || n.querySelector?.('product-card')) {
        scan(n.matches?.('product-card') ? n.parentElement || document : n);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
